import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { OAuthStrategyFactory, IOAuthStrategy } from '../services/oauthStrategies';
import { OAuthProvider } from '../services/oauthService';
import { GoogleSignInButton } from './GoogleSignInButton';

// 전역으로 OAuth 전략 초기화 상태를 관리
let strategiesInitialized = false;
const strategiesMap = new Map<OAuthProvider, IOAuthStrategy>();

const initializeStrategies = () => {
  if (strategiesInitialized) return;

  try {
    const googleStrategy = OAuthStrategyFactory.create(OAuthProvider.GOOGLE);
    googleStrategy.initialize();
    strategiesMap.set(OAuthProvider.GOOGLE, googleStrategy);

    const kakaoStrategy = OAuthStrategyFactory.create(OAuthProvider.KAKAO);
    kakaoStrategy.initialize();
    strategiesMap.set(OAuthProvider.KAKAO, kakaoStrategy);

    const naverStrategy = OAuthStrategyFactory.create(OAuthProvider.NAVER);
    naverStrategy.initialize();
    strategiesMap.set(OAuthProvider.NAVER, naverStrategy);

    strategiesInitialized = true;
  } catch (error) {
    console.error('[LoginModal] Failed to initialize OAuth strategies:', error);
  }
};

export const LoginModal: React.FC = React.memo(() => {
  const { showLoginModal, setShowLoginModal } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);

  useEffect(() => {
    if (showLoginModal && !strategiesInitialized) {
      initializeStrategies();
    }
  }, [showLoginModal]);

  const handleOAuthLogin = async (provider: OAuthProvider) => {
    try {
      setLoading(provider);
      const strategy = strategiesMap.get(provider);
      
      if (!strategy) {
        throw new Error(`Strategy not found for ${provider}`);
      }

      await strategy.login();
    } catch (error) {
      console.error(`[LoginModal] ${provider} login failed:`, error);
      alert(`${provider} 로그인에 실패했습니다.`);
      setLoading(null);
    }
  };

  if (!showLoginModal) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowLoginModal(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 mx-4 relative">
        {/* 우측 상단 X 닫기 버튼 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowLoginModal(false);
          }}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors z-10"
          type="button"
          aria-label="닫기"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* 제목 */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">로그인/회원가입</h1>
        
        {/* 부제 */}
        <p className="text-gray-600 text-sm mb-8">
          나만의 건강 주치의 허비서AI의 가족이 되어 보세요.
        </p>

        {/* 소셜 로그인 버튼들 */}
        <div className="space-y-3">
          {/* 구글 로그인 */}
          <GoogleSignInButton 
            onSignIn={() => handleOAuthLogin(OAuthProvider.GOOGLE)}
            isLoading={loading === OAuthProvider.GOOGLE}
          />

          {/* 카카오 로그인 */}
          <button
            className="w-full bg-yellow-300 hover:bg-yellow-400 disabled:bg-yellow-500 text-gray-800 font-semibold py-3 rounded-lg transition-colors duration-200 flex items-center justify-center gap-3"
            onClick={() => handleOAuthLogin(OAuthProvider.KAKAO)}
            disabled={loading !== null}
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 2C6.48 2 2 5.58 2 10c0 3.04 2.14 5.68 5.17 6.68-.1.52-.68 2.85-.73 3.07-.1.45.36.65.67.34.48-.49 2.56-2.61 3.49-3.57 1.19.14 2.43.21 3.7.21 5.52 0 10-3.58 10-8s-4.48-8-10-8z" />
            </svg>
            {loading === OAuthProvider.KAKAO ? '로그인 중...' : '카카오로 계속하기'}
          </button>

          {/* 네이버 로그인 */}
          <button
            className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors duration-200 flex items-center justify-center gap-3"
            onClick={() => handleOAuthLogin(OAuthProvider.NAVER)}
            disabled={loading !== null}
          >
            <div className="w-5 h-5 flex items-center justify-center font-bold text-lg">
              N
            </div>
            {loading === OAuthProvider.NAVER ? '로그인 중...' : '네이버로 계속하기'}
          </button>
        </div>

        {/* 약관 동의 */}
        <p className="text-gray-500 text-xs mt-6 text-center">
          로그인/회원가입함으로써 HerbotAI 이용약관 및 개인정보 처리방침에 동의합니다.
        </p>
      </div>
    </div>
  );
});

LoginModal.displayName = 'LoginModal';
